// Shared types and helpers for the expense screens (Expenses page, Add Expense form, event page).

export interface ExpenseCategoryOption {
  id: number;
  name: string;
  slug?: string;
  expenses_count?: number;
}

export interface PaymentMethodOption {
  value: string;
  label: string;
}

export interface ExpenseFormOptions {
  categories: ExpenseCategoryOption[];
  payment_methods: PaymentMethodOption[];
  receipt_required_above: number;
}

export interface ExpenseRecord {
  id: number;
  title: string;
  description?: string | null;
  amount: number | string;
  expense_date: string;
  expense_category_id?: number | null;
  category?: { id: number; name: string; slug?: string } | null;
  vendor_id?: number | null;
  vendor?: { id: number; name: string } | null;
  event_id?: number | null;
  event?: { id: number; name: string; start_date?: string; client?: { id: number; name: string } | null } | null;
  vendor_payment_id?: number | null;
  paid_to?: string | null;
  payment_method?: string | null;
  reference_number?: string | null;
  notes?: string | null;
  proof_path?: string | null;
  is_auto?: boolean;
  is_vendor_payment?: boolean;
  created_at?: string;
}

/**
 * Comparable form of a category name — mirrors ExpenseCategory::normalizeName on the server:
 * case, punctuation, "&" and "and" are ignored ("Food & Drinks" = "food and drinks" = "Food Drinks").
 */
export const normalizeCategoryName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/&/g, " and ")
    .split(/[^a-z0-9]+/)
    .filter((w) => w && w !== "and")
    .join("-");

/** Title the server builds when none is typed (Expense::defaultTitle). */
export const defaultExpenseTitle = (categoryName?: string | null, paidTo?: string | null): string => {
  const parts = [categoryName?.trim(), paidTo?.trim()].filter(Boolean);
  return parts.length ? parts.join(" · ") : "Expense";
};

/** Label for a stored payment-method key, using the master list when it has one. */
export const paymentMethodLabel = (value: string | null | undefined, options: PaymentMethodOption[] = []): string => {
  if (!value) return "";
  const known = options.find((o) => o.value === value);
  if (known) return known.label;
  if (value === "upi") return "UPI";
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

/** What the reference field should ask for with a given method; null = no reference (cash). */
export const referenceFieldFor = (method: string | null | undefined): { label: string; placeholder: string } | null => {
  const m = (method || "").toLowerCase();
  if (!m || m.includes("cash")) return null;
  if (m.includes("upi")) return { label: "UPI ref no.", placeholder: "e.g. 4271 8890 1123" };
  if (m.includes("cheque") || m.includes("check")) return { label: "Cheque no.", placeholder: "e.g. 004512" };
  if (m.includes("card")) return { label: "Card / slip ref", placeholder: "e.g. last 4 digits 4821" };
  return { label: "Transaction ID", placeholder: "e.g. NEFT / IMPS ref" };
};

/** Local calendar date `days` from today as YYYY-MM-DD. */
export const localDateOffset = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Unwrap list responses that may be paginated ({data:{data:[]}}) or plain ({data:[]}). */
export const listFrom = <T,>(res: any): T[] => {
  const d = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(d) ? d : [];
};
